// _src/js/islands/curved-text.js
import deferrableData from "deferrable-data";

// _src/js/components/curved-text.js
var curved_text_default = (text, waveWidth, waveHeight, direction, speed, enableAnimation, iconUrl, iconSize, mobileIconSize, mobileWaveWidth, mobileWaveHeight, desktopWaveThickness, mobileWaveThickness, mobileWaveVerticalOffset) => ({
  text,
  textForPath: text,
  baseSpacing: 1,
  direction,
  textOffset: 0,
  speed,
  waveWidth,
  intensityPercentage: 100,
  waveHeight,
  desktopWaveWidth: waveWidth,
  mobileWaveWidth: mobileWaveWidth || waveWidth,
  desktopWaveHeight: waveHeight,
  mobileWaveHeight: mobileWaveHeight || waveHeight,
  desktopWaveThickness: desktopWaveThickness || 80,
  mobileWaveThickness: mobileWaveThickness || desktopWaveThickness || 80,
  mobileWaveVerticalOffset: mobileWaveVerticalOffset || 0,
  waveVerticalOffset: 0,
  viewportWidth: window.innerWidth,
  enableAnimation,
  isDesktop: false,
  iconUrl,
  desktopIconSize: iconSize || 24,
  mobileIconSize: mobileIconSize || iconSize || 24,
  iconSize: iconSize || 24,
  iconSizeBase: iconSize || 24,
  iconPadding: 4,
  iconGap: 0,
  spacerCount: 0,
  computedGapWidth: 0,
  iconOffset: 0,
  iconCharIndex: 0,
  icons: [],
  textSpans: [],
  path: "",
  pathAreaAbove: "",
  pathAreaBelow: "",
  straightPath: "",
  totalTextSpans: 0,
  originalSpan: null,
  hasShadow: false,
  previousWindowWidth: 0,
  resizeHandler: null,
  afterResizeHandler: null,
  get textLength() {
    if (this.$refs.svgTextMeasure) {
      return this.$refs.svgTextMeasure.getComputedTextLength();
    }
    return this.$refs.textMeasureText ? this.$refs.textMeasureText.getBoundingClientRect().width : 0;
  },
  get baseTextLength() {
    if (this.$refs.svgBaseMeasure) {
      return this.$refs.svgBaseMeasure.getComputedTextLength();
    }
    return this.$refs.baseTextMeasure ? this.$refs.baseTextMeasure.getBoundingClientRect().width : 0;
  },
  get spaceWidth() {
    if (this.$refs.svgGapMeasure) {
      return this.$refs.svgGapMeasure.getComputedTextLength();
    }
    return this.$refs.spaceMeasure ? this.$refs.spaceMeasure.getBoundingClientRect().width : 0;
  },
  get waveIntensity() {
    const baseMidPoint = this.waveHeight / 2;
    return this.intensityPercentage * (this.waveHeight - baseMidPoint) / 100 + baseMidPoint + this.waveVerticalOffset;
  },
  get waveMidPoint() {
    return this.waveHeight / 2 + this.waveVerticalOffset;
  },
  get halfWaveWidth() {
    return this.waveWidth / 2;
  },
  get currentWaveHeight() {
    return this.waveHeight;
  },
  get currentWaveThickness() {
    return this.isDesktop ? this.desktopWaveThickness : this.mobileWaveThickness;
  },
  get totalTextSpanLength() {
    return this.totalTextSpans * this.textLength;
  },
  get totalPathLength() {
    return this.$refs.path.getTotalLength();
  },
  get iconHalfSize() {
    return this.iconSize / 2;
  },
  get gapWidth() {
    if (this.computedGapWidth) {
      return this.computedGapWidth;
    }
    const spaceWidth = this.spaceWidth || 0;
    return spaceWidth * (this.baseSpacing + this.spacerCount);
  },
  get iconCenterOffset() {
    if (this.iconOffset) {
      return this.iconOffset;
    }
    return this.baseTextLength + this.gapWidth / 2;
  },
  get threshold() {
    if (this.direction === "ltr") {
      return 0;
    } else {
      return this.totalTextSpanLength / 2;
    }
  },
  init() {
    if (this.$refs.shadowTextPath !== void 0) {
      this.hasShadow = true;
    }
    this.isDesktop = window.matchMedia("(min-width: 990px)").matches;
    this.applyResponsiveWaveSettings();
    this.createPath();
    this.$nextTick(() => {
      this.calculateText();
      this.play();
    });
    const mql = window.matchMedia("(min-width: 990px)");
    mql.onchange = (e) => {
      this.isDesktop = e.matches;
      this.reInit();
    };
    this.previousWindowWidth = window.innerWidth;
    this.resizeHandler = (event) => {
      let windowWidth = window.innerWidth;
      if (windowWidth !== this.previousWindowWidth) {
        this.paused = true;
        this.previousWindowWidth = windowWidth;
      }
    };
    this.afterResizeHandler = debounce(this.resizeComplete.bind(this), 300);
    window.addEventListener("resize", this.resizeHandler.bind(this));
    window.addEventListener("resize", this.afterResizeHandler.bind(this));
  },
  resizeComplete() {
    if (window.innerWidth > this.viewportWidth) {
      this.reInit();
    } else {
      if (this.paused) {
        this.play();
      }
    }
  },
  reInit() {
    this.paused = true;
    this.$refs.textPath.innerHTML = "";
    this.textSpans = [];
    if (this.$refs.iconGroup) {
      this.$refs.iconGroup.innerHTML = "";
      this.icons = [];
    }
    this.isDesktop = window.matchMedia("(min-width: 990px)").matches;
    this.applyResponsiveWaveSettings();
    this.viewportWidth = window.innerWidth;
    this.textOffset = 0;
    this.createPath();
    this.$nextTick(() => {
      this.calculateText();
      this.play();
    });
  },
  applyResponsiveWaveSettings() {
    if (this.isDesktop) {
      this.waveWidth = this.desktopWaveWidth;
      this.waveHeight = this.desktopWaveHeight;
      this.waveVerticalOffset = 0;
      this.iconSizeBase = this.desktopIconSize;
    } else {
      this.waveWidth = this.mobileWaveWidth;
      this.waveHeight = this.mobileWaveHeight;
      this.waveVerticalOffset = this.mobileWaveVerticalOffset;
      this.iconSizeBase = this.mobileIconSize;
    }
  },
  pause() {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.paused = true;
      }, 0);
    });
  },
  play() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !enableAnimation) {
      return;
    }
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.paused = false;
        requestAnimationFrame(this.animate.bind(this));
      }, 0);
    });
  },
  createPath() {
    this.startPoint = this.waveWidth * 2 * -1;
    this.waveSegments = Math.ceil(this.viewportWidth * 3 / this.waveWidth) + 1;
    this.firstControlPoint = this.startPoint + this.halfWaveWidth;
    this.path = "M" + this.startPoint + " " + this.waveMidPoint;
    let point = this.startPoint;
    let pathCommands = "";
    for (let i = 0; i < this.waveSegments; i++) {
      point = point + this.waveWidth;
      if (i === 0) {
        pathCommands += " Q" + (this.startPoint + this.halfWaveWidth) + " " + this.waveIntensity + ", " + point + " " + this.waveMidPoint;
      } else {
        pathCommands += " T" + point + " " + this.waveMidPoint;
      }
    }
    this.path = "M" + this.startPoint + " " + this.waveMidPoint + pathCommands;
    this.pathAreaAbove = "M" + this.startPoint + " 0 L" + this.startPoint + " " + this.waveMidPoint + pathCommands + " L" + point + " 0 Z";
    this.pathAreaBelow = "M" + this.startPoint + " " + this.waveMidPoint + pathCommands + " L" + point + " " + this.waveHeight + " L" + this.startPoint + " " + this.waveHeight + " Z";
  },
  prepareText() {
    if (!this.$refs.textMeasureText && !this.$refs.svgTextMeasure) {
      return;
    }
    this.iconSize = this.iconSizeBase;
    this.iconPadding = 4;
    if (this.$refs.svgBaseMeasure) {
      this.$refs.svgBaseMeasure.textContent = this.text;
    }
    if (this.$refs.svgGapMeasure) {
      this.$refs.svgGapMeasure.textContent = "\u00A0";
    }
    const spaceWidth = this.spaceWidth || 4;
    if (this.iconUrl) {
      const minGap = this.iconSize + this.iconPadding * 2;
      const baseGap = this.baseSpacing * spaceWidth;
      let targetGap = minGap;
      if (!this.isDesktop && this.baseTextLength) {
        const maxGap = Math.max(baseGap, this.baseTextLength * 0.25);
        targetGap = Math.min(minGap, maxGap);
      }
      const remaining = Math.max(0, targetGap - baseGap);
      this.spacerCount = Math.max(0, Math.ceil(remaining / spaceWidth));
      let gapChars = this.baseSpacing + this.spacerCount;
      if (gapChars % 2 !== 0) {
        gapChars += 1;
      }
      this.spacerCount = gapChars - this.baseSpacing;
      this.computedGapWidth = gapChars * spaceWidth;
      this.textForPath = `${this.text}${"\u00A0".repeat(gapChars)}`;
    } else {
      this.spacerCount = 0;
      this.computedGapWidth = this.baseSpacing * spaceWidth;
      this.textForPath = `${this.text}${"\u00A0".repeat(this.baseSpacing)}`;
    }
    const gapChars = this.baseSpacing + this.spacerCount;
    this.iconCharIndex = this.text.length + Math.max(0, Math.round(gapChars / 2));
    this.$refs.textMeasureText.textContent = this.textForPath;
    if (this.$refs.svgTextMeasure) {
      this.$refs.svgTextMeasure.textContent = this.textForPath;
    }
    this.iconOffset = this.iconUrl
      ? this.baseTextLength + this.iconPadding + this.iconHalfSize
      : 0;
  },
  calculateText() {
    this.prepareText();
    const baseSpans = Math.ceil(this.totalPathLength / this.textLength) + 1;
    const coverageFactor = this.isDesktop ? 1.2 : 2.4;
    const coverageSpans = Math.ceil(this.totalPathLength * coverageFactor / this.textLength) + 1;
    this.totalTextSpans = Math.max(baseSpans, coverageSpans);
    this.totalTextSpans = this.totalTextSpans % 2 !== 0 ? this.totalTextSpans + 1 : this.totalTextSpans;
    let tspanElement;
    for (let i = 0; i < this.totalTextSpans; i++) {
      tspanElement = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "tspan"
      );
      tspanElement.innerHTML = this.textForPath;
      tspanElement.setAttribute("alignment-baseline", "middle");
      tspanElement.setAttribute("aria-hidden", "true");
      this.$refs.textPath.appendChild(tspanElement);
      this.textSpans.push(tspanElement);
      if (this.hasShadow) {
        tspanElement = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "tspan"
        );
        tspanElement.innerHTML = this.textForPath;
        tspanElement.setAttribute("alignment-baseline", "middle");
        tspanElement.setAttribute("aria-hidden", "true");
        this.$refs.shadowTextPath.appendChild(tspanElement);
      }
    }
    this.createIcons();
    this.updateIcons();
    this.$nextTick(() => {
      if (this.direction === "ltr") {
        this.textOffset = this.totalTextSpanLength / 2 * -1;
      }
    });
  },
  createIcons() {
    if (!this.iconUrl || !this.$refs.iconGroup) {
      return;
    }
    for (let i = 0; i < this.totalTextSpans; i++) {
      const icon = document.createElementNS("http://www.w3.org/2000/svg", "image");
      icon.setAttribute("href", this.iconUrl);
      icon.setAttributeNS("http://www.w3.org/1999/xlink", "href", this.iconUrl);
      icon.setAttribute("width", this.iconSize);
      icon.setAttribute("height", this.iconSize);
      icon.setAttribute("preserveAspectRatio", "xMidYMid meet");
      icon.setAttribute("aria-hidden", "true");
      this.$refs.iconGroup.appendChild(icon);
      this.icons.push(icon);
    }
  },
  updateIcons() {
    if (!this.iconUrl || this.icons.length === 0) {
      return;
    }
    const minStride = this.iconSize + this.iconPadding * 2;
    const enforceMinStride = false;
    let lastVisiblePoint = null;
    for (let i = 0; i < this.icons.length; i++) {
      const tspan = this.textSpans[i];
      if (!tspan || typeof tspan.getNumberOfChars !== "function") {
        this.icons[i].setAttribute("visibility", "hidden");
        continue;
      }
      const charCount = tspan.getNumberOfChars();
      if (!charCount) {
        this.icons[i].setAttribute("visibility", "hidden");
        continue;
      }
      const iconCharIndex = Math.min(this.iconCharIndex, charCount - 1);
      let point;
      let centerPoint;
      try {
        point = tspan.getStartPositionOfChar(iconCharIndex);
        if (typeof tspan.getExtentOfChar === "function") {
          const extent = tspan.getExtentOfChar(iconCharIndex);
          centerPoint = {
            x: extent.x + extent.width / 2,
            y: extent.y + extent.height / 2
          };
        }
      } catch (error) {
        this.icons[i].setAttribute("visibility", "hidden");
        continue;
      }
      const anchorPoint = centerPoint || point;
      let visible = true;
      if (enforceMinStride && visible) {
        if (lastVisiblePoint !== null) {
          const dx = anchorPoint.x - lastVisiblePoint.x;
          const dy = anchorPoint.y - lastVisiblePoint.y;
          if (Math.hypot(dx, dy) < minStride) {
            visible = false;
          }
        }
      }
      if (!visible) {
        this.icons[i].setAttribute("visibility", "hidden");
        continue;
      }
      this.icons[i].setAttribute("visibility", "visible");
      let nextIndex = Math.min(iconCharIndex + 1, charCount - 1);
      if (nextIndex === iconCharIndex && iconCharIndex > 0) {
        nextIndex = iconCharIndex - 1;
      }
      let nextPoint = point;
      try {
        nextPoint = tspan.getStartPositionOfChar(nextIndex);
      } catch (error) {
        nextPoint = point;
      }
      const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x) * 180 / Math.PI;
      this.icons[i].setAttribute(
        "transform",
        `translate(${anchorPoint.x - this.iconHalfSize}, ${anchorPoint.y - this.iconHalfSize}) rotate(${angle}, ${this.iconHalfSize}, ${this.iconHalfSize})`
      );
      if (!this.isDesktop) {
        lastVisiblePoint = anchorPoint;
      }
    }
  },
  animate() {
    this.textOffset += 1 * this.speed;
    if (this.textOffset > this.threshold) {
      if (this.direction === "ltr") {
        this.textOffset = this.totalTextSpanLength / 2 * -1;
      } else {
        this.textOffset = 0;
      }
    }
    if (this.direction === "ltr") {
      this.$refs.textPath && this.$refs.textPath.setAttribute("startOffset", this.textOffset);
      if (this.hasShadow) {
        this.$refs.shadowTextPath && this.$refs.shadowTextPath.setAttribute(
          "startOffset",
          this.textOffset
        );
      }
    } else {
      this.$refs.textPath && this.$refs.textPath.setAttribute("startOffset", -this.textOffset);
      if (this.hasShadow) {
        this.$refs.shadowTextPath && this.$refs.shadowTextPath.setAttribute(
          "startOffset",
          -this.textOffset
        );
      }
    }
    this.updateIcons();
    if (!this.paused) {
      requestAnimationFrame(this.animate.bind(this));
    }
  },
  destroy() {
    window.removeEventListener("resize", this.resizeHandler);
    window.removeEventListener("resize", this.afterResizeHandler);
  }
});

// _src/js/islands/curved-text.js
deferrableData("CurvedText", curved_text_default);
